import React, { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { styled, TextField } from '@mui/material';
import { Person } from '@mui/icons-material';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { useForm, Controller } from 'react-hook-form';
import Page from 'components/new/Page';
import DropzoneComponent from 'components/Dropzone';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { adminUser as rules } from 'further-ui/validations';
import Button from 'components/Button';
import PageLoader from 'components/PageLoader';

const StyledLink = styled(Button)({
  width: '100%',
});

const FormColumn = styled(Column)({
  width: '100%',
  maxWidth: '600px',
});

const Profile: React.FC = () => {
  const { success, error } = useNotification();
  const { uploadFile } = useFileUpload();
  const [profilePicPreview, setProfilePicPreview] = useState<string>();
  const [profilePic, setProfilePic] = useState<string>();

  const {
    profile: { data, isLoading },
    updateProfile: { mutate, isPending },
    requestPasswordResetForSelf: {
      mutate: requestPasswordResetForSelf,
      isPending: isRequestPasswordResetPending,
    },
  } = useCurrentUser();

  useEffect(() => {
    if (!data) return;

    if (!data.profilePic.endsWith('default.jpg')) {
      setProfilePicPreview(data.profilePic);
      setProfilePic(data.profilePic);
    }
  }, [data]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.updateProfileInput),
    criteriaMode: 'all',
  });

  const onSubmit = async (formData) => {
    const diff = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] !== data?.[key]) {
        //@ts-expect-error
        acc.push(key);
      }
      return acc;
    }, []);

    mutate(
      {
        ...formData,
        profilePic: profilePic?.includes('.com/')
          ? profilePic.split('.com/')[1]
          : profilePic,
        updates: diff,
      },
      {
        onError: () => {
          error('Error when trying to update the profile.');
        },
        onSuccess: () => {
          success('Profile updated successfully.');
        },
      },
    );
  };

  const handleProfileImage = async (file) => {
    if (!file?.[0]?.path) return;

    const { data, error: uploadError } = await uploadFile(
      file[0],
      UploadType.UserImages,
    );
    if (data) {
      setProfilePicPreview(file?.[0]?.preview);
      setProfilePic(data.filePath);
    } else {
      error(uploadError.message);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Page heading="Profile" headingIcon={Person} spacing="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormColumn fullWidth spacing="lg">
          <Row spacing="md">
            <FormField label="First name">
              <Controller
                name="firstName"
                defaultValue={data?.firstName}
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    variant="outlined"
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message?.toString()}
                    fullWidth
                  />
                )}
              />
            </FormField>

            <FormField label="Last name">
              <Controller
                name="lastName"
                defaultValue={data?.lastName}
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    variant="outlined"
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message?.toString()}
                    fullWidth
                  />
                )}
              />
            </FormField>
          </Row>

          <FormField label="Email">
            <Controller
              name="email"
              defaultValue={data?.email}
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <TextField
                  {...rest}
                  inputRef={ref}
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.email}
                  helperText={errors.email?.message?.toString()}
                  fullWidth
                  disabled
                />
              )}
            />
          </FormField>

          <FormField label="Password">
            <Row spacing="sm">
              <TextField
                variant="outlined"
                size="small"
                value="********"
                disabled
                fullWidth
              />
              <StyledLink
                variant="outlined"
                onClick={() => requestPasswordResetForSelf()}
                loading={isRequestPasswordResetPending}
              >
                Send password reset email
              </StyledLink>
            </Row>
          </FormField>

          <FormField label="Profile image">
            <DropzoneComponent
              name="profilePic"
              defaultAcceptGroup="images"
              onDropFile={(files) => handleProfileImage(files)}
              onRemove={() => {
                setProfilePicPreview(undefined);
                setProfilePic(undefined);
              }}
              files={
                profilePicPreview
                  ? [{ preview: profilePicPreview, fileName: '' }]
                  : []
              }
            />
          </FormField>

          <Row justify="flex-end">
            <Button
              type="submit"
              size="large"
              color="primary"
              variant="contained"
              loading={isPending}
              disabled={isPending}
            >
              Update
            </Button>
          </Row>
        </FormColumn>
      </form>
    </Page>
  );
};

export default Profile;
